import { useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"

/**
 * OAuth 2.0 callback page for NetSuite integration.
 * Opened as a popup from NetSuiteSettingsDialog.
 * Extracts auth code and state from URL, sends them to the parent window via postMessage,
 * then the parent window handles the token exchange (where the Cognito session exists).
 */
export default function NetSuiteCallback() {
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<"processing" | "success" | "error">("processing")
  const [errorMessage, setErrorMessage] = useState("")
  const hasRun = useRef(false)

  useEffect(() => {
    if (hasRun.current) return
    hasRun.current = true

    const code = searchParams.get("code")
    const state = searchParams.get("state")
    const error = searchParams.get("error")

    if (error) {
      const description = searchParams.get("error_description") || error
      setStatus("error")
      setErrorMessage(description)
      window.opener?.postMessage(
        { type: "netsuite-oauth-callback", success: false, error: description },
        window.location.origin
      )
      return
    }

    if (!code || !state) {
      setStatus("error")
      setErrorMessage("認可コードまたは state パラメータがありません")
      window.opener?.postMessage(
        { type: "netsuite-oauth-callback", success: false, error: "パラメータ不足" },
        window.location.origin
      )
      return
    }

    // Send code and state to parent window for token exchange
    window.opener?.postMessage(
      { type: "netsuite-oauth-callback", success: true, code, state },
      window.location.origin
    )
    setStatus("success")
    setTimeout(() => window.close(), 1500)
  }, [searchParams])

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="mx-auto max-w-sm space-y-4 text-center">
        {status === "processing" && (
          <>
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <p className="text-sm text-muted-foreground">NetSuite に接続中...</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm font-medium">認可が完了しました</p>
            <p className="text-xs text-muted-foreground">このウィンドウは自動的に閉じます。</p>
          </>
        )}
        {status === "error" && (
          <>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-sm font-medium">接続に失敗しました</p>
            <p className="text-xs text-muted-foreground">{errorMessage}</p>
            <button
              onClick={() => window.close()}
              className="text-xs text-primary underline hover:no-underline"
            >
              ウィンドウを閉じる
            </button>
          </>
        )}
      </div>
    </div>
  )
}
